import {
  createWorketFromSrc,
  registeredWorklets,
} from "./audioworklet-registry";

export class AudioStreamer {
  public audioQueue: Float32Array[] = [];
  private isPlaying: boolean = false;
  private sampleRate: number = 24000;
  private baseBufferSize: number = 7680;
  private bufferSize: number = this.baseBufferSize;
  private processingBuffer: Float32Array = new Float32Array(0);
  private scheduledTime: number = 0;
  public gainNode: GainNode;
  public source: AudioBufferSourceNode;
  private isStreamComplete: boolean = false;
  private checkInterval: number | null = null;
  private initialBufferTime: number = 0.1;
  private endOfQueueAudioSource: AudioBufferSourceNode | null = null;
  private isBackgroundTab: boolean = false;
  private visibilityHandler: () => void = () => {};
  private isSafari: boolean;

  public onComplete = () => {};

  constructor(public context: AudioContext) {
    this.gainNode = this.context.createGain();
    this.source = this.context.createBufferSource();
    this.gainNode.connect(this.context.destination);
    this.addPCM16 = this.addPCM16.bind(this);

    // Detect Safari
    this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    if (this.isSafari) {
      // Initialize visibility change handler only for Safari
      this.visibilityHandler = () => this.handleVisibilityChange();
      document.addEventListener("visibilitychange", this.visibilityHandler);

      // Initial visibility check
      this.isBackgroundTab = document.hidden;
      this.updateBufferSize();
    }
  }

  private handleVisibilityChange() {
    if (!this.isSafari) return;

    this.isBackgroundTab = document.hidden;

    if (document.hidden) {
      // Completely clear and rebuild the audio pipeline when going to background
      this.stop(false); // Don't clear queue

      // Increase buffer dramatically for background
      this.bufferSize = this.baseBufferSize * 4;

      // Force immediate rebuild of audio pipeline
      if (this.audioQueue.length > 0) {
        this.scheduledTime = this.context.currentTime + 0.2;
        this.isPlaying = true;
        requestAnimationFrame(() => this.scheduleNextBuffer());
      }
    } else {
      // Return to normal buffer size when back in foreground
      this.bufferSize = this.baseBufferSize;

      if (this.isPlaying) {
        // Rebuild pipeline with normal buffering
        this.stop(false);
        this.scheduledTime = this.context.currentTime + 0.1;
        this.isPlaying = true;
        requestAnimationFrame(() => this.scheduleNextBuffer());
      }
    }
  }

  private updateBufferSize() {
    if (!this.isSafari) {
      this.bufferSize = this.baseBufferSize;
      return;
    }

    this.bufferSize = this.isBackgroundTab
      ? this.baseBufferSize * 2
      : this.baseBufferSize;
  }

  private rescheduleAudio() {
    if (!this.isPlaying || !this.isSafari) return;

    this.stop(false); // Don't clear the queue
    this.scheduledTime = this.context.currentTime + this.initialBufferTime;
    this.isPlaying = true;
    this.scheduleNextBuffer();
  }

  addPCM16(chunk: Uint8Array) {
    const float32Array = new Float32Array(chunk.length / 2);
    const dataView = new DataView(chunk.buffer);

    for (let i = 0; i < chunk.length / 2; i++) {
      try {
        const int16 = dataView.getInt16(i * 2, true);
        float32Array[i] = int16 / 32768;
      } catch (e) {
        console.error(e);
      }
    }

    const newBuffer = new Float32Array(
      this.processingBuffer.length + float32Array.length,
    );
    newBuffer.set(this.processingBuffer);
    newBuffer.set(float32Array, this.processingBuffer.length);
    this.processingBuffer = newBuffer;

    while (this.processingBuffer.length >= this.bufferSize) {
      const buffer = this.processingBuffer.slice(0, this.bufferSize);
      this.audioQueue.push(buffer);
      this.processingBuffer = this.processingBuffer.slice(this.bufferSize);
    }

    if (!this.isPlaying) {
      this.isPlaying = true;
      this.scheduledTime = this.context.currentTime + this.initialBufferTime;
      this.scheduleNextBuffer();
    }
  }

  private createAudioBuffer(audioData: Float32Array): AudioBuffer {
    const audioBuffer = this.context.createBuffer(
      1,
      audioData.length,
      this.sampleRate,
    );
    audioBuffer.getChannelData(0).set(audioData);
    return audioBuffer;
  }

  private scheduleNextBuffer() {
    if (this.isSafari && this.isBackgroundTab) {
      // Much more aggressive buffering for Safari background
      const BACKGROUND_AHEAD_TIME = 2.0; // Buffer 2 seconds ahead
      const MINIMUM_BUFFER_COUNT = 4; // Keep at least 4 buffers queued

      while (
        this.audioQueue.length > 0 &&
        (this.scheduledTime <
          this.context.currentTime + BACKGROUND_AHEAD_TIME ||
          this.audioQueue.length < MINIMUM_BUFFER_COUNT)
      ) {
        const audioData = this.audioQueue.shift()!;
        const audioBuffer = this.createAudioBuffer(audioData);
        const source = this.context.createBufferSource();

        if (this.audioQueue.length === 0) {
          if (this.endOfQueueAudioSource) {
            this.endOfQueueAudioSource.onended = null;
          }
          this.endOfQueueAudioSource = source;
          source.onended = () => {
            if (
              !this.audioQueue.length &&
              this.endOfQueueAudioSource === source
            ) {
              this.endOfQueueAudioSource = null;
              this.onComplete();
            }
          };
        }

        source.buffer = audioBuffer;
        source.connect(this.gainNode);

        // Connect worklets if any
        const worklets = registeredWorklets.get(this.context);
        if (worklets) {
          Object.entries(worklets).forEach(([workletName, graph]) => {
            const { node, handlers } = graph;
            if (node) {
              source.connect(node);
              node.connect(this.context.destination);
              node.port.onmessage = function (ev: MessageEvent) {
                handlers.forEach((handler) => handler.call(node.port, ev));
              };
            }
          });
        }

        const startTime = Math.max(
          this.scheduledTime,
          this.context.currentTime + 0.1,
        );
        source.start(startTime);
        this.scheduledTime = startTime + audioBuffer.duration;
      }
    } else {
      // Original scheduling logic for non-Safari or foreground
      const SCHEDULE_AHEAD_TIME = 0.2;

      while (
        this.audioQueue.length > 0 &&
        this.scheduledTime < this.context.currentTime + SCHEDULE_AHEAD_TIME
      ) {
        // ... (rest of your original scheduling code)
        const audioData = this.audioQueue.shift()!;
        const audioBuffer = this.createAudioBuffer(audioData);
        const source = this.context.createBufferSource();

        if (this.audioQueue.length === 0) {
          if (this.endOfQueueAudioSource) {
            this.endOfQueueAudioSource.onended = null;
          }
          this.endOfQueueAudioSource = source;
          source.onended = () => {
            if (
              !this.audioQueue.length &&
              this.endOfQueueAudioSource === source
            ) {
              this.endOfQueueAudioSource = null;
              this.onComplete();
            }
          };
        }

        source.buffer = audioBuffer;
        source.connect(this.gainNode);

        // Connect worklets if any
        const worklets = registeredWorklets.get(this.context);
        if (worklets) {
          Object.entries(worklets).forEach(([workletName, graph]) => {
            const { node, handlers } = graph;
            if (node) {
              source.connect(node);
              node.connect(this.context.destination);
              node.port.onmessage = function (ev: MessageEvent) {
                handlers.forEach((handler) => handler.call(node.port, ev));
              };
            }
          });
        }

        const startTime = Math.max(
          this.scheduledTime,
          this.context.currentTime,
        );
        source.start(startTime);
        this.scheduledTime = startTime + audioBuffer.duration;
      }
    }

    // Schedule next check
    if (this.audioQueue.length === 0 && this.processingBuffer.length === 0) {
      if (this.isStreamComplete) {
        this.isPlaying = false;
        if (this.checkInterval) {
          clearInterval(this.checkInterval);
          this.checkInterval = null;
        }
      } else {
        if (!this.checkInterval) {
          this.checkInterval = window.setInterval(() => {
            if (
              this.audioQueue.length > 0 ||
              this.processingBuffer.length >= this.bufferSize
            ) {
              this.scheduleNextBuffer();
            }
          }, 100) as unknown as number;
        }
      }
    } else {
      const nextCheckTime =
        (this.scheduledTime - this.context.currentTime) * 1000;
      setTimeout(
        () => this.scheduleNextBuffer(),
        Math.max(0, nextCheckTime - 50),
      );
    }
  }
  stop(clearQueue: boolean = true) {
    this.isPlaying = false;
    this.isStreamComplete = true;

    if (clearQueue) {
      this.audioQueue = [];
      this.processingBuffer = new Float32Array(0);
    }

    this.scheduledTime = this.context.currentTime;

    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    this.gainNode.gain.linearRampToValueAtTime(
      0,
      this.context.currentTime + 0.1,
    );

    setTimeout(() => {
      this.gainNode.disconnect();
      this.gainNode = this.context.createGain();
      this.gainNode.connect(this.context.destination);
    }, 200);
  }

  async resume() {
    if (this.context.state === "suspended") {
      await this.context.resume();
    }
    this.isStreamComplete = false;
    this.scheduledTime = this.context.currentTime + this.initialBufferTime;
    this.gainNode.gain.setValueAtTime(1, this.context.currentTime);
  }

  complete() {
    this.isStreamComplete = true;
    if (this.processingBuffer.length > 0) {
      this.audioQueue.push(this.processingBuffer);
      this.processingBuffer = new Float32Array(0);
      if (this.isPlaying) {
        this.scheduleNextBuffer();
      }
    } else {
      this.onComplete();
    }
  }

  cleanup() {
    if (this.isSafari) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
    }
  }
}

export type GetAudioContextOptions = AudioContextOptions & {
  id?: string;
};

const map: Map<string, AudioContext> = new Map();

export const audioContext: (
  options?: GetAudioContextOptions,
) => Promise<AudioContext> = (() => {
  const didInteract = new Promise((res) => {
    window.addEventListener("pointerdown", res, { once: true });
    window.addEventListener("keydown", res, { once: true });
  });

  return async (options?: GetAudioContextOptions) => {
    try {
      const a = new Audio();
      a.src =
        "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";
      await a.play();
      if (options?.id && map.has(options.id)) {
        const ctx = map.get(options.id);
        if (ctx) {
          return ctx;
        }
      }
      const ctx = new AudioContext(options);
      if (options?.id) {
        map.set(options.id, ctx);
      }
      return ctx;
    } catch (e) {
      await didInteract;
      if (options?.id && map.has(options.id)) {
        const ctx = map.get(options.id);
        if (ctx) {
          return ctx;
        }
      }
      const ctx = new AudioContext(options);
      if (options?.id) {
        map.set(options.id, ctx);
      }
      return ctx;
    }
  };
})();

export const blobToJSON = (blob: Blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result) {
        const json = JSON.parse(reader.result as string);
        resolve(json);
      } else {
        reject("oops");
      }
    };
    reader.readAsText(blob);
  });

// // Usage example:
// const audioStreamer = new AudioStreamer();
//
// // In your streaming code:
// function handleChunk(chunk: Uint8Array) {
//   audioStreamer.handleChunk(chunk);
// }
//
// // To start playing (call this in response to a user interaction)
// await audioStreamer.resume();
//
// // To stop playing
// // audioStreamer.stop();
